import * as THREE from 'three';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { GenerateMeshBVHWorker } from 'three-mesh-bvh/src/workers/GenerateMeshBVHWorker.js';
import modelCache from './ModelCache';

export default class ModelLoader {
    constructor(scene, camera) {
        this.scene = scene;
        this.camera = camera;
    }

    async fetchModelWithCache(url, onPhase) {
        const cacheResponse = await modelCache.fetchModelFromCache(url);

        if (cacheResponse.byteLength > 0){
            // Baked model already cached → drop the now-superseded raw scan PLY (no-op for raw URLs).
            await modelCache.evictSupersededRawAsync(url);
            return cacheResponse;
        }

        // Cache miss → going to the network. Switch to the download phase up front (indeterminate
        // until we have a Content-Length) so it shows during connection/time-to-first-byte too,
        // not just while the body streams. readBodyWithProgress refines it to a real percentage.
        if (onPhase) onPhase('download', null);

        console.log(`Downloading model from network: ${url}`);
        const networkResponse = await fetch(url);

        if (!networkResponse.ok) {
            throw new Error(`Network error loading model: ${url}`);
        }

        const arrayBuffer = await this.readBodyWithProgress(networkResponse, onPhase);

        // The body was consumed while streaming, so re-wrap the buffer in a Response to cache it.
        // Persist the byte length as Content-Length so the cache table / device-budget logic can read
        // the model size back without re-reading the whole body. byteLength is the decoded size (more
        // reliable than the network Content-Length, which is absent under chunked/gzip transfer).
        const cacheHeaders = new Headers({
            'Content-Type': 'application/octet-stream',
            'Content-Length': String(arrayBuffer.byteLength),
        });
        await modelCache.saveModelToCache(url, new Response(arrayBuffer, { headers: cacheHeaders }));

        // Baked model now cached → drop the now-superseded raw scan PLY (no-op for raw URLs).
        await modelCache.evictSupersededRawAsync(url);

        return arrayBuffer;
    }

    // Read a response body to an ArrayBuffer, reporting download progress as a 0..1 fraction
    // via onPhase('download', fraction). Falls back to a plain read (fraction = null,
    // i.e. indeterminate) when streaming or Content-Length is unavailable.
    async readBodyWithProgress(response, onPhase) {
        const contentLength = Number(response.headers.get('Content-Length')) || 0;

        if (!response.body || typeof response.body.getReader !== 'function') {
            if (onPhase) onPhase('download', null);
            return await response.arrayBuffer();
        }

        if (onPhase) onPhase('download', contentLength ? 0 : null);

        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;

        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value);
            received += value.length;
            if (onPhase && contentLength) onPhase('download', received / contentLength);
        }

        const merged = new Uint8Array(received);
        let offset = 0;
        for (const chunk of chunks) {
            merged.set(chunk, offset);
            offset += chunk.length;
        }
        return merged.buffer;
    }

    // buildBvh: build the BVH pick-mesh + helper used for raycasting (marker placement). Only the
    // primary/base scan needs it; supplementary scans are display-only, so pass false to skip the
    // (expensive) BVH build for them.
    async loadPLYModelAsPointCloud(modelSrc, onPhase, buildBvh = true) {
        const arrayBuffer = await this.fetchModelWithCache(modelSrc, onPhase);

        // Parse + colour-pack + bounding-sphere off the main thread. This is the load bottleneck and
        // it serialises across scans on the main thread; the worker frees the UI and lets multiple
        // scans parse in parallel. Uses local vars (not this.pointCloud) for the return value so
        // concurrent (parallel) loads can't clobber each other across the await.
        if (onPhase) onPhase('processing');

        let parsed;
        try {
            parsed = await this.parsePlyInWorker(arrayBuffer);
        } catch (err) {
            // Worker unavailable/failed (e.g. older browser, bundling edge case). Fall back to the
            // old main-thread parse so the model still loads (slower, blocks the UI) rather than
            // failing. The original buffer was transferred into the worker, so re-fetch from cache.
            if (process.env.NODE_ENV === 'development') {
                console.warn('DEV: PLY worker parse failed — falling back to main-thread parse', err);
            }
            const fallbackBuffer = await this.fetchModelWithCache(modelSrc);
            parsed = this.parsePlyOnMainThread(fallbackBuffer);
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(parsed.position, parsed.positionItemSize));
        if (parsed.color) {
            geometry.setAttribute('color', new THREE.BufferAttribute(parsed.color, parsed.colorItemSize, parsed.colorNormalized));
        }
        if (parsed.normal) {
            geometry.setAttribute('normal', new THREE.BufferAttribute(parsed.normal, parsed.normalItemSize));
        }
        if (parsed.boundingSphere) {
            const bs = parsed.boundingSphere;
            geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(bs.x, bs.y, bs.z), bs.radius);
        }

        const material = new THREE.PointsMaterial({ size: 0.02, vertexColors: true });
        const pointCloud = new THREE.Points(geometry, material);
        pointCloud.matrixAutoUpdate = false;
        this.pointCloud = pointCloud;
        this.scene.add(pointCloud);

        if (!buildBvh) {
            return { pointCloud, bvhMesh: null, helper: null };
        }

        const bvhGeometry = geometry.clone();
        const vertexCount = bvhGeometry.attributes.position.count;

        // Degenerate-triangle index (i,i,i) makes the point cloud raycastable as a mesh via
        // three-mesh-bvh. Build a typed array directly (avoids a multi-million-element JS array).
        const index = new Uint32Array(vertexCount * 3);
        for (let i = 0; i < vertexCount; i++) {
            index[i * 3] = i;
            index[i * 3 + 1] = i;
            index[i * 3 + 2] = i;
        }

        bvhGeometry.setIndex(new THREE.BufferAttribute(index, 1));
        const bvhMesh = new THREE.Mesh(bvhGeometry, new THREE.MeshBasicMaterial());
        this.bvhMesh = bvhMesh;

        // Build the BVH off the main thread (Web Worker) so the point cloud can render immediately
        // instead of waiting on the multi-second blocking compute. Without the BVH the bvhMesh's
        // degenerate-triangle index would let the default raycast hit it but iterate every triangle,
        // freezing the tab — so we mute the instance raycast until the worker resolves, then delete
        // the override so the prototype's acceleratedRaycast resumes.
        bvhMesh.raycast = () => {};

        const worker = new GenerateMeshBVHWorker();
        worker.generate(bvhGeometry)
            .then((bvh) => {
                bvhGeometry.boundsTree = bvh;
                delete bvhMesh.raycast;
            })
            .catch((err) => {
                // Picking stays disabled for this scan — better than a multi-second main-thread
                // freeze if we fell back to the sync build mid-session.
                if (process.env.NODE_ENV === 'development') {
                    console.warn('DEV: async BVH build failed — picking disabled for this scan', err);
                }
            })
            .finally(() => {
                worker.dispose();
            });

        return { pointCloud, bvhMesh, helper: null };
    }

    // Parse a PLY ArrayBuffer in a Web Worker (off the main thread). Returns the transferred typed
    // arrays + a precomputed bounding sphere. The buffer is transferred (zero-copy) — safe because
    // fetchModelWithCache has already persisted its own copy to the cache and the buffer isn't used
    // again on this thread. A fresh short-lived worker per call keeps it simple and bounded by the
    // (small) scan count; the loader fires these in parallel so the scans parse concurrently.
    parsePlyInWorker(arrayBuffer) {
        return new Promise((resolve, reject) => {
            let worker;
            try {
                worker = new Worker(new URL('./plyParseWorker.js', import.meta.url), { type: 'module' });
            } catch (e) {
                reject(e);
                return;
            }

            worker.onmessage = (event) => {
                worker.terminate();
                const data = event.data;
                if (data && data.ok) resolve(data.result);
                else reject(new Error(data && data.error ? data.error : 'PLY parse worker failed'));
            };
            worker.onerror = (err) => {
                worker.terminate();
                reject(err);
            };

            worker.postMessage({ buffer: arrayBuffer }, [arrayBuffer]);
        });
    }

    // Main-thread fallback for parsePlyInWorker — same output shape (typed arrays + Uint8 colour).
    // Bounding sphere is left undefined so three computes it lazily; only hit on the rare worker
    // failure path, so the extra main-thread pass is an acceptable trade for resilience.
    parsePlyOnMainThread(arrayBuffer) {
        const geometry = new PLYLoader().parse(arrayBuffer);
        const position = geometry.getAttribute('position');
        const color = geometry.getAttribute('color');
        const normal = geometry.getAttribute('normal');

        const result = {
            position: position.array,
            positionItemSize: position.itemSize,
            count: position.count,
        };

        if (color) {
            if (color.array instanceof Float32Array) {
                const src = color.array;
                const packed = new Uint8Array(src.length);
                for (let i = 0; i < src.length; i++) {
                    packed[i] = Math.max(0, Math.min(255, Math.round(src[i] * 255)));
                }
                result.color = packed;
                result.colorNormalized = true;
            } else {
                result.color = color.array;
                result.colorNormalized = color.normalized ?? true;
            }
            result.colorItemSize = color.itemSize;
        }

        if (normal) {
            result.normal = normal.array;
            result.normalItemSize = normal.itemSize;
        }

        return result;
    }

    async loadPLYModelsAsMesh(modelSrc, onLoad) {
        const loader = new PLYLoader();
        const arrayBuffer = await this.fetchModelWithCache(modelSrc);

        try {
            const geometry = loader.parse(arrayBuffer);
            geometry.computeVertexNormals();
            geometry.center();

            if (geometry.hasAttribute('normal')) geometry.deleteAttribute('normal');
            if (geometry.hasAttribute('uv')) geometry.deleteAttribute('uv');

            const material = new THREE.MeshStandardMaterial({
                color: 0x808080,
                metalness: 0.5,
                roughness: 0.8,
                vertexColors: true,
                flatShading: true,
            });

            this.mesh = new THREE.Mesh(geometry, material);
            this.mesh.rotation.x = -Math.PI / 2;
            this.scene.add(this.mesh);

            this.adjustCameraToModel(this.mesh);
            if (onLoad) onLoad(this.mesh);

        } catch (err) {
            console.error('Error parsing PLY model as mesh:', err);
        }
    }

    adjustCameraToModel(model) {
        const boundingBox = new THREE.Box3().setFromObject(model);
        const size = boundingBox.getSize(new THREE.Vector3());
        const center = boundingBox.getCenter(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = this.camera.fov * (Math.PI / 180);
        const cameraZ = Math.abs(maxDim / (2 * Math.tan(fov / 2)));

        this.camera.position.set(center.x, center.y, cameraZ * 1.5);
        this.camera.lookAt(center);

        this.camera.updateProjectionMatrix();
    }
}
